import { useEffect } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import { routes, RouteParams } from 'constants/routes'
import { useModalContext } from 'containers/ModalManager/ModalContext'
import ShareSettingsModal from 'modules/shares/components/ShareSettingsModal'
import { ShareWithoutVersion } from 'modules/versioning/ShareVersionContext/ShareVersionContext'
import { VersionFragment } from 'gqlTypes'

/**
 * DocumentView can be accessed with a ?settings=:share-id URL query param
 * which is used as a deeplink to the document settings modal.
 * This param is checked when the components mounts and is removed from
 * the URL instantly after.
 */
export function useHandleSettingsQueryParamIfNeeded(
  share: ShareWithoutVersion,
  currentVersion: VersionFragment | null,
  latestVersion: VersionFragment
) {
  const history = useHistory()
  const location = useLocation<{ unsubscribe: boolean }>()
  const { shareID } = useParams<
    RouteParams<'SHARE_VIEW'> | RouteParams<'SHARE_PAGE_VIEW'>
  >()
  const { showModal } = useModalContext()

  const handleSettingsUrlParam = () => {
    const urlParams = new URLSearchParams(location.search)
    const showSettings = urlParams.get('settings') === shareID

    if (showSettings) {
      /* Prevent a refresh from showing the settings again */
      history.replace(routes.SHARE_VIEW.create({ shareID }))

      const userCanSeeSettings =
        share.userCanUpdateSettings || share.userIsOwner

      if (userCanSeeSettings) {
        showModal(ShareSettingsModal, {
          share,
          openPermissions: urlParams.get('tab') === 'sharing',
          version: {
            currentVersion,
            latestVersion,
          },
        })
      }
    }
  }

  // Run on component mounts only
  useEffect(() => {
    handleSettingsUrlParam()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
